import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap, delay, concatMap } from 'rxjs/operators';
import { MedRegWbtDto, MedRegBewilligungDto, GetMedRegPersonDetailedOutput, LiaTypeServiceProxy, ExternalSystemEnum, ProfessionRegisterAffiliationServiceProxy, ProfessionRegisterAffiliationDto, GetNaregPersonDetailedOutput, GetPsyRegPersonDetailedOutput, GetPsyRegPersonOutput, GetNaregPersonOutput, MedRegWholePersonDetailedDto, PsyRegPersonDetailedDto, NaregPersonDetailedDto, NaregPersonDto, PsyRegPersonDto, HealthRegisterIdentificationStatus, HealthRegServiceProxy, ApplicationType, PerformHealthRegisterCheckInput } from '@shared/service-proxies/service-proxies';
import { DatePipe } from '@angular/common';
import { DateTime } from 'luxon';
import { LocalizationService } from 'abp-ng2-module';
import { HealthRegisterDiploma } from './dto/health-register-diploma';
import { HealthRegisterPostgraduateTitle } from './dto/health-register-postgraduate-title';
import { HealthRegisterPostgraduateTitlePrivateLaw } from './dto/health-register-postgraduate-title-private-law';
import { MedRegService } from './medreg.service';
import { PsyRegService } from './psyreg.service';
import { NaregService } from './nareg.service';
import { MedRegFullPersonInfo } from './dto/medreg-full-person-info';
import { PsyRegFullPersonInfo } from './dto/psyreg-full-person-info';
import { NaregFullPersonInfo } from './dto/nareg-full-person-info';
import { AppComponentBase } from '@shared/common/app-component-base';

export interface IHealthRegisterFullPersonInfo {

    get healthRegisterLabel(): string;
    get SearchSuccessfulMessage(): string;
    get SearchFailedMessageKey(): string;
    personWasFound(): boolean;
    get firstName(): string;
    get lastName(): string;
    get fullName(): string;
    get birthName(): string;
    get gender(): string;
    get birthYear(): string;
    get birthDate(): string;
    get placeOfOrigin(): string;
    get nationality1(): string;
    get nationality2(): string;
    get ahv(): string;
    get gln(): string;
    get languageSkills(): string[];
    get languageSkillsAsCommaSeparatedList(): string;
    get queryTime(): DateTime;
    get diplomas(): string[];
    getProfessionDiplomaForComparison(professionId: string): HealthRegisterDiploma;
    getPostgraduateTitlesForComparison(): HealthRegisterPostgraduateTitle[];
    getPostgraduateTitlesPrivateLawForComparison(): HealthRegisterPostgraduateTitlePrivateLaw[];
    get postgraduateTitles(): string[];
    get normalLicences(): string[];
    hasLicenceInThisCanton(): boolean;
    get dl90Licences(): string[];
    get cantonalLicenceRestrictions(): string[];
    //    getLicences(): MedRegBewilligungDto[];
    get hasSensitiveData(): boolean;
    get hasSensitiveDataCantonalLaw(): boolean;
    //getPostgraduateTitlesForPerson(personId: number): Observable<string[] | string>;
}
@Injectable()
export class HealthRegisterService extends AppComponentBase {

    professionToHealthRegisterSystemAffiliations: ProfessionRegisterAffiliationDto[];
    healthRegisterSystem: ExternalSystemEnum;
    healthRegisterService: MedRegService | NaregService | PsyRegService;
    isAuthority = false;

    constructor(
        protected datePipe: DatePipe,
        private injector: Injector,
        private medRegService: MedRegService,
        private psyRegService: PsyRegService,
        private naregService: NaregService,
        private professionRegisterService: ProfessionRegisterAffiliationServiceProxy,
        private healthRegServiceProxy: HealthRegServiceProxy
    ) {
        super(injector);

        this.isAuthority = !this.permission.isGranted('Pages.Applicant');
    }

    // Called from guard
    loadProfessionRegisterAffiliations(): Observable<ProfessionRegisterAffiliationDto[]> {
        return this.professionRegisterService.getAll().pipe(tap(affiliations => {
            this.professionToHealthRegisterSystemAffiliations = affiliations;
        }));
    }

    setHealthRegisterService(professionId: string) {

        if (professionId === 'Profession-Psychologist') {
            this.healthRegisterSystem = ExternalSystemEnum.PsyReg;
        } else {
            this.healthRegisterSystem = this.professionToHealthRegisterSystemAffiliations.find(a => a.id === professionId).externalSystem;
        };

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                this.healthRegisterService = this.medRegService;
                break;
            case ExternalSystemEnum.PsyReg:
                this.healthRegisterService = this.psyRegService;
                break;
            case ExternalSystemEnum.Nareg:
                this.healthRegisterService = this.naregService;
                break;
            default:
                this.healthRegisterService = null;
                break;
        }
    }

    getPerson(professionId: string, personId: number): Observable<MedRegWholePersonDetailedDto | PsyRegPersonDto | NaregPersonDto | string> {

        this.setHealthRegisterService(professionId);

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return this.medRegService.getPerson(personId);
            case ExternalSystemEnum.PsyReg:
                return this.psyRegService.getPerson(personId);
            case ExternalSystemEnum.Nareg:
                return this.naregService.getPerson(personId);
            default:
                return of();
        }
    }

    getPersonByProfessionAndGln(professionId: string, gln: string): Observable<MedRegWholePersonDetailedDto | PsyRegPersonDto | NaregPersonDto | string> {

        this.setHealthRegisterService(professionId);

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return this.medRegService.getPersonByGln(gln);
            case ExternalSystemEnum.PsyReg:
                return this.psyRegService.getPersonByGln(gln);
            case ExternalSystemEnum.Nareg:
                return this.naregService.getPersonByGln(gln);
            default:
                return of();
        }
    }

    getPersonDetailed(professionId: string, personId: number): Observable<MedRegWholePersonDetailedDto | PsyRegPersonDetailedDto | NaregPersonDetailedDto | string> {

        this.setHealthRegisterService(professionId);

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return this.medRegService.getPersonDetailed(personId);
            case ExternalSystemEnum.PsyReg:
                return this.psyRegService.getPersonDetailed(personId);
            case ExternalSystemEnum.Nareg:
                return this.naregService.getPersonDetailed(personId);
            default:
                return of();
        }
    }

    getPersonDetailedByProfessionAndGln(professionId: string, gln: string): Observable<MedRegWholePersonDetailedDto | PsyRegPersonDetailedDto | NaregPersonDetailedDto | string> {

        this.setHealthRegisterService(professionId);

        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return this.medRegService.getPersonDetailedByGln(gln);
            case ExternalSystemEnum.PsyReg:
                return this.psyRegService.getPersonDetailedByGln(gln);
            case ExternalSystemEnum.Nareg:
                return this.naregService.getPersonDetailedByGln(gln);
            default:
                return of();
        }
    }

    getProfessionsForRegisterSearch(): string[] {
        return this.professionToHealthRegisterSystemAffiliations.map(a => a.id);
    }

    getFullHealthRegPersonInfoByProfessionAndGln(professionId: string, gln: string): Observable<IHealthRegisterFullPersonInfo | string> {

        this.healthRegisterSystem = this.professionToHealthRegisterSystemAffiliations.find(a => a.id === professionId)?.externalSystem;

        if (this.isAuthority) {
            switch (this.healthRegisterSystem) {
                case ExternalSystemEnum.MedReg:
                    return this.medRegService.getPersonDetailedByGln(gln).pipe(map((medRegPerson: MedRegWholePersonDetailedDto) => new MedRegFullPersonInfo(medRegPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                case ExternalSystemEnum.PsyReg:
                    return this.psyRegService.getPersonDetailedByGln(gln).pipe(map((psyRegPerson: PsyRegPersonDetailedDto) => new PsyRegFullPersonInfo(psyRegPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                case ExternalSystemEnum.Nareg:
                    return this.naregService.getPersonDetailedByGln(gln).pipe(map((naregPerson: NaregPersonDetailedDto) => new NaregFullPersonInfo(naregPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                default:
                    return of();
            }
        } else {
            switch (this.healthRegisterSystem) {
                case ExternalSystemEnum.MedReg:
                    return this.medRegService.getPersonByGln(gln).pipe(map((medRegPerson: MedRegWholePersonDetailedDto) => new MedRegFullPersonInfo(medRegPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                case ExternalSystemEnum.PsyReg:
                    return this.psyRegService.getPersonByGln(gln).pipe(map((psyRegPerson: PsyRegPersonDetailedDto) => new PsyRegFullPersonInfo(psyRegPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                case ExternalSystemEnum.Nareg:
                    return this.naregService.getPersonByGln(gln).pipe(map((naregPerson: NaregPersonDetailedDto) => new NaregFullPersonInfo(naregPerson, this.datePipe, this.injector) as IHealthRegisterFullPersonInfo));
                default:
                    return of();
            }
        }
    }

    performHealthRegisterCheck(sponsorshipTypeId: string, professionId: string, gln: string, applicationType: ApplicationType, step: number): Observable<HealthRegisterIdentificationStatus> {
        return this.healthRegServiceProxy.performHealthRegisterCheck(new PerformHealthRegisterCheckInput({gln: gln, professionId: professionId, sponsorshipTypeId: sponsorshipTypeId, applicationType: applicationType, step: step}));
    }

    getPersonDetailedByGln(gln: string): Observable<MedRegWholePersonDetailedDto | PsyRegPersonDetailedDto | NaregPersonDetailedDto | string> {
        // Since we don't know which register to query, we start with MedReg...
        this.healthRegisterSystem = ExternalSystemEnum.MedReg;
        this.healthRegisterService = this.medRegService;
        return this.healthRegisterService.getPersonDetailedByGln(gln).pipe(concatMap(medRegPerson => {
            if (medRegPerson) {
                return of(medRegPerson);
            } else {
                this.healthRegisterSystem = ExternalSystemEnum.PsyReg;
                this.healthRegisterService = this.psyRegService;
                return this.healthRegisterService.getPersonDetailedByGln(gln);
            }
        }));
    }

    get healthRegisterLabel(): string {
        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return 'MedReg';
            case ExternalSystemEnum.PsyReg:
                return 'PsyReg';
            case ExternalSystemEnum.Nareg:
                return 'Nareg';
            default:
                return null;
        }
    }

    getHealthRegisterSystemByProfession(professionId: string): ExternalSystemEnum {
        return this.professionToHealthRegisterSystemAffiliations.find(a => a.id === professionId).externalSystem;
    }

    getHealthRegisterLabelByProfession(professionId: string): string {
        this.healthRegisterSystem = this.professionToHealthRegisterSystemAffiliations.find(a => a.id === professionId).externalSystem;
        switch (this.healthRegisterSystem) {
            case ExternalSystemEnum.MedReg:
                return 'MedReg';
            case ExternalSystemEnum.PsyReg:
                return 'PsyReg';
            case ExternalSystemEnum.Nareg:
                return 'Nareg';
            default:
                return null;
        }
    }

    get SearchSuccessfulMessage(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.SearchSuccessfulMessage;
    }

    get SearchFailedMessageKey(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.SearchFailedMessageKey;
    }

    personWasFound(): boolean {
        return this.healthRegisterService?.personWasFound();
    }

    get firstName(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.firstName;
    }

    get lastName(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.lastName;
    }

    get fullName(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.fullName;
    }

    get birthName(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.birthName;
    }

    get gender(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.gender;
    }

    get birthYear(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.birthYear;
    }

    get birthDate(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.birthDate;
    }

    get placeOfOrigin(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.placeOfOrigin;
    }

    get nationality1(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.nationality1;
    }

    get nationality2(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.nationality2;
    }

    get ahv(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.ahv;
    }

    get gln(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.gln;
    }

    get languageSkills(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.languageSkills;
    }

    get languageSkillsAsCommaSeparatedList(): string {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.languageSkillsAsCommaSeparatedList;
    }

    get queryTime(): DateTime {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.queryTime;
    }

    get diplomas(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.diplomas;
    }

    getProfessionDiplomaForComparison(professionId: string): HealthRegisterDiploma {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.getProfessionDiplomaForComparison(professionId);
    }

    getPostgraduateTitlesForComparison(): HealthRegisterPostgraduateTitle[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.getPostgraduateTitlesForComparison();
    }


    getPostgraduateTitlesPrivateLawForComparison(): HealthRegisterPostgraduateTitlePrivateLaw[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.getPostgraduateTitlesPrivateLawForComparison();
    }

    get postgraduateTitles(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.postgraduateTitles;
    }

//    private displayCorrectWbtDate(wbt: MedRegWbtDto): string {
//        return this.healthRegisterService.displayCorrectWbtDate(wbt);
//    }


    get normalLicences(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.normalLicences;
    }

    hasLicenceInThisCanton(): boolean {
        let licences = this.healthRegisterService.getLicences();
        let hasLicenceInThisCanton = licences.some(x => x.kanton.sironaId === abp.canton.code);
        return hasLicenceInThisCanton;
    }

    get dl90Licences(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.dl90Licences;
    }

    get cantonalLicenceRestrictions(): string[] {

        if (this.healthRegisterService == null) {
            return null;
        }

        return this.healthRegisterService.cantonalLicenceRestrictions;
    }

//    getLicences(): MedRegBewilligungDto[] {
//        return this.healthRegisterService.getLicences();
//    }

//    getLanguage(healthRegId: number): Promise<string> {
//        return this.healthRegisterService.getLanguage(healthRegId);
//    }

    get hasSensitiveData(): boolean {
        return this.healthRegisterService.hasSensitiveData;
    }

    get hasSensitiveDataCantonalLaw(): boolean {
        return this.healthRegisterService.hasSensitiveDataCantonalLaw;
    }

//    private getDate(licence: MedRegBewilligungDto): DateTime {
//        return this.healthRegisterService.getDate(licence);
//    }

    // getPostgraduateTitlesForPerson(personId: number): Observable<string[] | string> {
    //     return this.healthRegisterService.getPostgraduateTitlesForPerson(personId);
    // }

//    clearCacheOnIdChange(healthRegId: number): void {
//        return this.healthRegisterService.clearCacheOnIdChange(healthRegId);
//    }
}
